import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Clock, Send, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { SITE } from "@/constants/site";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Seo } from "@/components/site/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  ndis_number: "",
  enquiry_type: "general",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim()) return "Please enter your email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Please enter a valid email address.";
    if (!form.message.trim()) return "Please tell us a little about your situation.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setStatus("error");
      setErrorMsg(v);
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        ndis_number: form.ndis_number.trim() || null,
        enquiry_type: form.enquiry_type,
        message: form.message.trim(),
      });
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      setErrorMsg(typeof detail === "string" ? detail : "Please check your details and try again.");
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Kernected Disability"
        description="Get in touch with Kernected Disability Support Services. Tell us a little about your situation and we'll reply within one business day with clear next steps."
        path="/contact"
      />
      <section className="container-narrow py-16 md:py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-[#1B3A6B]" aria-hidden="true" />
            Get in touch · Brisbane
          </span>
          <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.75rem] font-medium tracking-tight text-slate-900 leading-[1.05]">
            Let's start a conversation
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">
            Tell us a little about your situation and we'll reply within one
            business day with clear next steps.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          {/* CONTACT INFO */}
          <aside
            className="lg:col-span-2 bg-[#1B3A6B] text-white rounded-3xl p-8 md:p-10"
            data-testid="contact-info-panel"
          >
            <h2 className="font-display text-2xl font-semibold">Contact details</h2>
            <p className="mt-3 text-slate-100/90">
              Prefer email or a phone callback? We'll meet you where you are.
            </p>

            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#7AB4DF]">
                    Email
                  </p>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-email"
                    className="break-all text-base md:text-lg font-medium hover:underline"
                  >
                    {SITE.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#7AB4DF]">
                    Response time
                  </p>
                  <p className="text-base md:text-lg">Within 1 business day</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#7AB4DF]">
                    Service area
                  </p>
                  <p className="text-base md:text-lg">Brisbane · in person and Australia-wide remote</p>
                </div>
              </li>
            </ul>

            <div className="mt-10 p-5 bg-white/5 border border-white/10 rounded-xl">
              <p className="text-sm text-slate-100/90 leading-relaxed">
                If your enquiry is urgent, please mention this in your message
                and we will prioritise our response.
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-3 bg-white border border-slate-200/70 rounded-3xl p-8 md:p-10">
            {status === "success" ? (
              <div
                data-testid="contact-form-success"
                className="text-center py-10"
              >
                <div className="h-14 w-14 mx-auto rounded-full bg-[#F0F4F8] flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-[#1B3A6B]" />
                </div>
                <h2 className="mt-5 font-display text-2xl md:text-3xl font-semibold text-slate-900">
                  Thank you, we've got it
                </h2>
                <p className="mt-3 text-slate-700 max-w-md mx-auto">
                  We'll be in touch within one business day. In the meantime,
                  feel free to email us directly at{" "}
                  <a
                    href={`mailto:${SITE.email}`}
                    className="text-[#1B3A6B] underline"
                  >
                    {SITE.email}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="contact-form-reset"
                  onClick={() => setStatus("idle")}
                  className="mt-7 inline-flex items-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-6 py-3 rounded-lg font-medium"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-800">
                      Your name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="form-input-name"
                      placeholder="Jane Smith"
                      className="mt-1.5 h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-800">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="form-input-email"
                      placeholder="you@example.com"
                      className="mt-1.5 h-11"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-slate-800">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="form-input-phone"
                      placeholder="04XX XXX XXX"
                      className="mt-1.5 h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ndis_number" className="text-slate-800">
                      NDIS number (optional)
                    </Label>
                    <Input
                      id="ndis_number"
                      name="ndis_number"
                      value={form.ndis_number}
                      onChange={update("ndis_number")}
                      data-testid="form-input-ndis"
                      placeholder="If known"
                      className="mt-1.5 h-11"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-800">
                    What's your enquiry about? *
                  </Label>
                  <Select
                    value={form.enquiry_type}
                    onValueChange={(v) => setForm((f) => ({ ...f, enquiry_type: v }))}
                  >
                    <SelectTrigger
                      className="mt-1.5 h-11"
                      data-testid="form-select-enquiry-type"
                      aria-label="Enquiry type"
                    >
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General enquiry</SelectItem>
                      <SelectItem value="support_coordination">
                        Support Coordination
                      </SelectItem>
                      <SelectItem value="plan_implementation">
                        Plan implementation help
                      </SelectItem>
                      <SelectItem value="switching_providers">
                        Switching providers
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-800">
                    Tell us about your situation *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="form-input-message"
                    placeholder="A few sentences are plenty. We'll follow up to learn more."
                    className="mt-1.5"
                  />
                </div>

                {status === "error" && (
                  <div
                    role="alert"
                    data-testid="contact-form-error"
                    className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4"
                  >
                    <AlertTriangle className="h-5 w-5 mt-0.5" />
                    <p className="text-sm">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="form-submit-button"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] disabled:opacity-70 disabled:cursor-not-allowed px-7 py-3.5 rounded-lg font-medium transition-colors"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send enquiry
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500 pt-2">
                  By submitting, you agree to be contacted about your enquiry.
                  We never share your details with third parties.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
